/**
 * Settings (slice 1b spec §6.3; slice 2a part 2 §2; slice 3b §5.1): the
 * title, then four tabs, each at its own URL. AI (`/settings`) is model
 * routing and a card per connected provider (`ai-settings.tsx`); Tools
 * (`/settings/tools`) is everyone's; Integrations (`/settings/integrations`)
 * is each person's own apps plus the shared ones (slice 6a §6.4), with
 * Browse apps under it (`/settings/integrations/apps`) and the owner's What
 * asks first (`/settings/integrations/ask-first`, slice 3); Messaging (`/settings/messaging`, slice 2e)
 * is each person's own; Usage (`/settings/usage`) is the owner's. After the
 * tabs, links to Routines, Activity, Memory and People, which keep their own pages.
 */
import { Link } from "@crew/ui/components/link";
import { PageTitle } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { useEffect, type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import type { Me } from "../../app/me";
import { AiSettings } from "./ai-settings";
import { AskFirstPage } from "./ask-first-page";
import { BrowseApps } from "./browse-apps";
import { IntegrationsTab } from "./integrations-tab";
import { pagesFor, tabOf, tabsFor, type Tab } from "./settings-view";
import { clearStarted, rememberStarted } from "./integrations-api";
import { listenForSignIns } from "./signin-tab";
import { MessagingTab } from "../messaging/messaging-tab";
import { ToolsTab } from "./tools-tab";
import { UsageTab } from "./usage-tab";

/**
 * A sign-in this tab started finished in its own tab (`signin-tab.ts`): this
 * tab goes to Integrations with its outcome, as if it had come back here,
 * so the page shows the same banner; that tab then closes.
 */
function useSignInReturns() {
  const navigate = useNavigate();
  useEffect(
    () =>
      listenForSignIns((query, started) => {
        // The banner names the connect this sign-in was for, not a later one's.
        if (started) rememberStarted(started);
        else clearStarted();
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) if (v) params.set(k, v);
        void navigate(`/settings/integrations?${params.toString()}`);
      }),
    [navigate],
  );
}

/** A page under Integrations: Browse apps, or the owner's What asks first. */
type Sub = "apps" | "ask-first";

export function SettingsPage({ me, sub }: { me: Me; sub?: Sub }) {
  useSignInReturns();
  const { tab: segment } = useParams();
  const role = me.user.role;
  const tab = sub ? "integrations" : tabOf(segment, role);
  // A tab that isn't one (or Usage, for a member) goes back to AI.
  if (!tab) return <Navigate to="/settings" replace />;
  // What asks first is the owner's (slice 3 §6).
  if (sub === "ask-first" && role !== "owner") {
    return <Navigate to="/settings/integrations" replace />;
  }
  return (
    <main className="flex flex-col gap-6 px-4 pt-6 pb-16 sm:px-6 md:px-12 md:pt-10">
      <header className="flex flex-col gap-1">
        <PageTitle>Settings</PageTitle>
        <p className="text-sm text-muted-foreground">
          How your crew thinks, and what it can reach.
        </p>
      </header>
      <nav
        aria-label="Settings"
        className="-mx-4 -mt-3 flex max-w-[1112px] gap-2 overflow-x-auto border-b border-border px-2 [scrollbar-width:none] sm:mx-0 sm:w-full sm:gap-6 sm:px-0"
      >
        {tabsFor(role).map((t) => (
          <Link
            key={t.tab}
            href={t.href}
            aria-current={t.tab === tab ? "page" : undefined}
            className={cn(
              "-mb-px flex h-11 shrink-0 items-center border-b-2 px-2 pt-0.5 text-[15px] sm:px-0",
              t.tab === tab
                ? "border-foreground font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </Link>
        ))}
        <span aria-hidden className="my-3 w-px shrink-0 bg-border" />
        {pagesFor(role).map((p) => (
          <Link
            key={p.href}
            href={p.href}
            data-settings-page
            className="-mb-px flex h-11 shrink-0 items-center border-b-2 border-transparent px-2 pt-0.5 text-[15px] text-muted-foreground hover:text-foreground sm:px-0"
          >
            {p.label}
          </Link>
        ))}
      </nav>
      <TabBody tab={tab} me={me} sub={sub} />
    </main>
  );
}

const SUBS: Record<Sub, (me: Me) => ReactNode> = {
  apps: () => <BrowseApps />,
  "ask-first": (me) => <AskFirstPage timeZone={me.workspace.timezone} />,
};

function TabBody({ tab, me, sub }: { tab: Tab; me: Me; sub?: Sub }) {
  if (tab === "integrations") {
    return (
      <div className="w-full max-w-[1112px]">
        {sub ? SUBS[sub](me) : <IntegrationsTab me={me} />}
      </div>
    );
  }
  if (tab === "messaging") {
    return (
      <div className="w-full max-w-[1112px]">
        <MessagingTab me={me} />
      </div>
    );
  }
  if (tab === "tools") {
    return (
      <div className="w-full max-w-4xl">
        <ToolsTab owner={me.user.role === "owner"} computer={me.computer} />
      </div>
    );
  }
  if (tab === "usage") {
    return (
      <div className="w-full max-w-4xl">
        <UsageTab />
      </div>
    );
  }
  return <AiTab me={me} />;
}

function AiTab({ me }: { me: Me }) {
  const ai = me.computer?.ai;
  if (!ai) return <p className="text-body text-muted-foreground">No computer yet.</p>;
  return (
    <section
      // The agent page's "AI settings →" lands here (slice 1e spec §4).
      id="ai"
      aria-label="AI"
      className="flex w-full max-w-2xl scroll-mt-6 flex-col gap-5"
      data-ai-status={ai.status}
    >
      {me.user.role === "owner" ? (
        <AiSettings ai={ai} timezone={me.workspace.timezone} />
      ) : (
        <p className="text-body text-muted-foreground">The workspace owner manages the AI.</p>
      )}
    </section>
  );
}
